import { useEffect, useState } from 'react';

const WING_IMG = 'https://customer-assets.emergentagent.com/job_jay-pernell-sports/artifacts/z7u9j3pl_white%20wings_edited.png';

const SESSION_KEY = 'dl_splash_shown_v1';
const VISIBLE_MS = 900;      // Time the splash holds on-screen before exit (iter354: 1700→900 for faster perceived load)
const EXIT_MS = 350;          // Tail-off animation length

/**
 * LaunchSplash — cinematic cold-open, shown once per session.
 * iter270 · Rewritten with pure CSS keyframes. This was the ONLY eager
 * consumer of framer-motion, which pinned ~45KB gz of animation runtime
 * inside main.js and slowed every first paint on mobile. Same visual:
 * FL·wings·YT fade in, wings flap, LEAGUE + credit fade up, then the
 * whole composition blurs/scales out revealing the app.
 */
const LaunchSplash = () => {
  const [show, setShow] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') return false;
      const params = new URLSearchParams(window.location.search);
      if (params.get('nosplash') === '1') return false;
      const path = window.location.pathname || '';
      if (path.startsWith('/admin') || path.startsWith('/Penelopesims') || path.startsWith('/login')) return false;
    } catch { /* allow */ }
    return true;
  });
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    // iter—launch sound removed per Jay's request. The visual splash
    // stays; the synthesized "wing flap + chime" sting no longer plays.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const t1 = setTimeout(() => setLeaving(true), VISIBLE_MS);
    const t2 = setTimeout(() => setShow(false), VISIBLE_MS + EXIT_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      document.body.style.overflow = prev;
      try { document.documentElement.classList.remove('dl-splash-pending'); } catch { /* ignore */ }
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      key="launch-splash"
      className="fixed inset-0 z-[2000] bg-black flex items-start justify-center select-none pt-[34vh] sm:pt-[36vh]"
      style={leaving ? { animation: `dlSplashOut ${EXIT_MS}ms cubic-bezier(0.4,0,0.2,1) forwards` } : undefined}
      data-testid="launch-splash"
      aria-hidden="true"
    >
      <style>{`
        @keyframes dlSplashOut { to { opacity: 0; } }
        @keyframes dlSplashStackOut { to { opacity: 0; transform: scale(1.45); filter: blur(8px); } }
        @keyframes dlSplashFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dlSplashCredit { from { opacity: 0; } to { opacity: 0.55; } }
        @keyframes dlSplashWings {
          0%   { opacity: 0; transform: scale(0.92) translateY(0); }
          35%  { opacity: 1; transform: scale(1) translateY(0); }
          60%  { opacity: 1; transform: scale(1.04) translateY(-3px); }
          85%  { opacity: 1; transform: scale(0.98) translateY(1px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      <div
        className="relative flex items-center justify-center"
        style={leaving ? { animation: `dlSplashStackOut ${EXIT_MS}ms cubic-bezier(0.16,1,0.3,1) forwards` } : undefined}
      >
        {/* Row 1: FL · WINGS · YT */}
        <div className="flex items-center justify-center">
          <span
            className="font-display font-black text-white text-[64px] sm:text-[88px] md:text-[112px] leading-none tracking-tight"
            style={{ opacity: 0, animation: 'dlSplashFadeIn 350ms cubic-bezier(0.22,1,0.36,1) 50ms forwards' }}
          >
            FL
          </span>
          <img
            src={WING_IMG}
            alt=""
            aria-hidden="true"
            className="h-[40px] sm:h-[54px] md:h-[68px] w-auto mx-2 sm:mx-3"
            style={{
              opacity: 0,
              // invert/drop-shadow kept static so iOS Safari never drops
              // the filter mid-animation (black-on-black wings bug).
              filter: 'brightness(0) invert(1) drop-shadow(0 0 22px rgba(255,255,255,0.35))',
              animation: 'dlSplashWings 1350ms cubic-bezier(0.22,1,0.36,1) forwards',
            }}
          />
          <span
            className="font-display font-black text-white text-[64px] sm:text-[88px] md:text-[112px] leading-none tracking-tight"
            style={{ opacity: 0, animation: 'dlSplashFadeIn 350ms cubic-bezier(0.22,1,0.36,1) 50ms forwards' }}
          >
            YT
          </span>
        </div>

        {/* Row 2: LEAGUE — anchored below so the wings stay optically centered */}
        <span
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 sm:mt-3 font-display font-black text-white text-[22px] sm:text-[30px] md:text-[40px] uppercase whitespace-nowrap"
          style={{ letterSpacing: '0.32em', opacity: 0, animation: 'dlSplashFadeIn 450ms cubic-bezier(0.22,1,0.36,1) 200ms forwards' }}
        >
          League
        </span>
        <span
          className="absolute left-1/2 -translate-x-1/2 top-full mt-16 sm:mt-20 md:mt-24 text-[7px] sm:text-[8px] md:text-[9px] uppercase tracking-[0.5em] text-white/55 whitespace-nowrap text-center"
          style={{ opacity: 0, animation: 'dlSplashCredit 550ms cubic-bezier(0.22,1,0.36,1) 700ms forwards' }}
          data-testid="splash-founder-credit"
        >
          by Jay Pernell
        </span>
      </div>
    </div>
  );
};

export default LaunchSplash;
